import { sanitizeFontFamily } from "@babackapp/editor-blocks-library/fonts";
import { cn } from "@babackapp/ui/lib/utils";
import { Body, Container, Head, Html, Preview, Section, Tailwind } from "@react-email/components";
import type { ReactNode } from "react";
import { EditorContentMaxWidth } from "../../constants/editor-constants";
import { useWebFontStylesheets } from "../../hooks/use-web-font-stylesheets";


export interface EmailMainContainerProps {
  children: ReactNode;
  viewport?: "desktop" | "mobile";
  backgroundColor?: string;
  preview?: string;
  tailwindConfig?: {
    theme: {
      extend: {
        colors: Record<string, string>;
      };
    };
  };
  isEmailRenderer?: boolean;
  isEditor?: boolean;
  isWeb?: boolean;
  editorClassName?: string;
  editorStyle?: React.CSSProperties;
  contentMaxWidth?: number;
  /** Theme font stack. */
  fontFamily?: string | null;
  /**
   * Web font stylesheets of the theme and the blocks, loaded in the page in
   * editor mode. Sent emails get them from transactional-emails, hidden from
   * Outlook.
   */
  fontUrls?: string[];
}


/**
 * Shared main container component for both email renderer and editor
 * Handles:
 * - Background color
 * - Viewport-responsive width (480px desktop, 375px mobile)
 * - Viewport-responsive padding (0 desktop, 16px mobile)
 * - Top and bottom spacing (32px)
 */
export function EmailMainContainer({
  children,
  viewport = "desktop",
  backgroundColor = "#f0f0f0",
  preview,
  tailwindConfig,
  isEmailRenderer = false,
  isEditor = false,
  isWeb = false,
  editorClassName,
  editorStyle,
  contentMaxWidth,
  fontFamily,
  fontUrls = [],
}: EmailMainContainerProps) {
  const resolvedMaxWidth = contentMaxWidth ?? EditorContentMaxWidth;
  const themeFont = sanitizeFontFamily(fontFamily);
  useWebFontStylesheets(isEditor ? fontUrls : []);
  if (isEmailRenderer) {
    return (
      <Tailwind config={tailwindConfig}>
        <Html lang="en" dir="ltr" style={{ colorScheme: "light only" }}>
          <Head>
            <style>
              {`
                :root {
                  color-scheme: light only;
                  supported-color-schemes: light;
                }
                body {
                  color-scheme: light only;
                  supported-color-schemes: light;
                }
                @media only screen and (max-width: 600px) {
                  .stack-on-mobile {
                    width: 100% !important;
                    max-width: 100% !important;
                    display: block !important;
                    min-height: 0 !important;
                  }
                  .email-card-padding {
                    padding: 16px !important;
                  }
                }${
                  // Outlook does not always pass the body font down to tables.
                  // Adds nothing without a theme font, not even a line break.
                  themeFont
                    ? `\n                body, table, td, th { font-family: ${themeFont}; }`
                    : ""
                }
              `}
            </style>
          </Head>
          {preview && <Preview>{preview}</Preview>}
          <Body
            style={{
              ...main,
              ...(themeFont && { fontFamily: themeFont }),
              colorScheme: "light only",
            }}
            className="bg-background"
          >
            <Container
              className="max-w-full"
              style={{
                maxWidth: resolvedMaxWidth,
                width: "100%",
                paddingLeft: 16,
                paddingRight: 16,
                margin: "auto", // Ensure the container is centered
              }}
            >
              <Section className="h-8" />
              {children}
              <Section className="h-8" />
            </Container>
          </Body>
        </Html>
      </Tailwind>
    );
  }

  if (isEditor) {
    return (
      <>
        <style>
          {`
            /* Apply stacking on actual mobile screens */
            @media only screen and (max-width: 600px) {
              .stack-on-mobile {
                width: 100% !important;
                max-width: 100% !important;
                display: block !important;
              }
            }

            /* Apply stacking when viewport is set to mobile in editor */
            ${
            viewport === "mobile"
              ? `
              .stack-on-mobile {
                width: 100% !important;
                max-width: 100% !important;
                display: block !important;
                min-height: 0 !important;
              }
            `
              : ""
          }
          `}
        </style>
        <div
          className={cn(
            "group/editor w-full border flex-1 flex items-center justify-center flex-col rounded-xl",
            editorClassName,
          )}
          style={{
            backgroundColor,
            ...(themeFont && { fontFamily: themeFont }),
            ...editorStyle,
          }}
        >
          <div
            className="mx-auto flex flex-col justify-center items-center"
            style={{
              paddingLeft: 16,
              paddingRight: 16,
              maxWidth: resolvedMaxWidth,
              width: "100%",
              minHeight: "50vh",
            }}
          >
            <div className="h-8" />
            {children}
            <div className="h-8" />
          </div>
        </div>
      </>
    );
  }

  if (isWeb) {
    return (
      <div
        className="w-full min-h-screen flex flex-col"
        style={{ backgroundColor }}
      >
        {children}
      </div>
    );
  }

  return <>{children}</>;
}


const main = {
  fontFamily:
    "-apple-system,BlinkMacSystemFont,\"Segoe UI\",Roboto,Oxygen-Sans,Ubuntu,Cantarell,\"Helvetica Neue\",sans-serif",
};

export { Preview } from "@react-email/components";
